import {
  type ArcualCurrency,
  type ArcualPaymentProfileBankDetails,
  ConnectedAccountStatus,
  getPaymentProfileService,
  type PaymentProfile,
  PaymentProfileEvent,
  type PaymentProfileEventPayload,
  type PaymentProfileSseEventPayload,
  type PaymentSystem,
  PayoutMethodStatus,
  Permission,
  type SupportedCurrency,
  TransferCapabilityStatus,
} from '@art-basel/artbasel-lib-platform';
import { useAuthorizationStore } from '@/stores/AuthorizationStore/AuthorizationStore';
import { isIbanCurrency } from '@/utils/currencies';
import { handleHttpError } from '@/utils/errorHandlers';
import { getRoute, RoutePaths } from '@/views/gallery/route-paths';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useAmplitudeFeatureFlags } from '@/composables/useAmplitudeFeatureFlags';

const { featureFlags } = useAmplitudeFeatureFlags();

const paymentProfileStoreKey = 'PaymentProfileStore';

export const usePaymentProfileStore = defineStore(
  paymentProfileStoreKey,
  () => {
    // todo future we could have multiple profiles so this might need to be an array
    const paymentProfile = ref<PaymentProfile>();
    const verificationLink = ref<string>();
    const hasDismissedPaymentProfileBanner = ref<boolean>(false);
    const currentPaymentSystem = ref<PaymentSystem>();
    const paymentProfileBankDetails = ref<ArcualPaymentProfileBankDetails>();

    const isPaymentProfileVerified = computed(
      () =>
        paymentProfile.value?.connectedAccountStatus === ConnectedAccountStatus.VERIFIED &&
        paymentProfile.value?.payoutMethodStatus === PayoutMethodStatus.VERIFIED &&
        paymentProfile.value?.transferCapabilityStatus === TransferCapabilityStatus.VERIFIED,
    );
    // todo maybe we load this from the BE later
    const salesCurrencies = ref<Array<string>>(['CHF', 'EUR', 'GBP', 'USD']);
    const payoutCurrencies = ref<SupportedCurrency[]>([]);

    const returnHost = `${window.location.protocol}//${window.location.host}`;
    const refreshUri = `${returnHost}${getRoute(RoutePaths.INVENTORY)}`;
    const returnUri = `${returnHost}${getRoute(RoutePaths.INVENTORY)}`;

    const loadPaymentProfile = async () => {
      try {
        const result = await getPaymentProfileService().getPaymentProfile();
        paymentProfile.value = result;
        return result;
      } catch (e) {
        handleHttpError(e);
        return Promise.reject(e);
      } finally {
        checkPaymentStatus();
      }
    };

    const loadPaymentBankDetails = async (reset = false) => {
      try {
        if (reset) {
          paymentProfileBankDetails.value = undefined;
        }

        if (paymentProfileBankDetails.value) {
          return paymentProfileBankDetails.value;
        }

        const result = await getPaymentProfileService().getPaymentBankDetails();
        paymentProfileBankDetails.value = result;
        return result;
      } catch (e) {
        handleHttpError(e);
        return Promise.reject(e);
      }
    };

    const handlePaymentProfileUpdatedEvent = (data: PaymentProfileEventPayload) => {
      if (paymentProfile.value) {
        paymentProfile.value = { ...paymentProfile.value, ...data };
        checkPaymentStatus();
      } else {
        loadPaymentProfile();
      }
    };

    const checkPaymentStatus = () => {
      const { hasPermission } = useAuthorizationStore();

      if (!hasPermission(Permission.CREATE_SALES_AGREEMENT)) return;

      if (featureFlags.value.isPaymentSettingsEnabled) return;
    };

    const updatePaymentProfile = async (updatedPaymentProfile: PaymentProfile) => {
      try {
        const result = await getPaymentProfileService().updatePaymentProfile({
          payoutCountry: updatedPaymentProfile.payoutCountry,
          presentmentCurrency: updatedPaymentProfile.presentmentCurrency,
          countryOfTaxResidence: updatedPaymentProfile.countryOfTaxResidence,
          settlementCurrency: updatedPaymentProfile.settlementCurrency,
        });
        paymentProfile.value = { ...paymentProfile.value, ...updatedPaymentProfile };
        return result;
      } catch (e) {
        handleHttpError(e);
        return Promise.reject(e);
      }
    };

    const onboardPaymentProfile = async (onboardingPaymentProfile: PaymentProfile) => {
      try {
        const result = await getPaymentProfileService().onboardPaymentProfile({
          accountHolderName: onboardingPaymentProfile.accountHolderName,
          accountNumber: onboardingPaymentProfile.accountNumber,
          routingNumber: !isIbanCurrency(onboardingPaymentProfile.settlementCurrency, payoutCurrencies.value)
            ? onboardingPaymentProfile.routingNumber
            : undefined,
          refreshUri,
          returnUri,
        });
        verificationLink.value = result.verificationUri;
        return result;
      } catch (e) {
        handleHttpError(e);
        return Promise.reject(e);
      }
    };

    const loadPayoutCurrencies = async (countryCode: string) => {
      try {
        const result = await getPaymentProfileService().getSupportedPayoutCurrencies(countryCode);
        payoutCurrencies.value = result.supportedCurrencies;
        return result;
      } catch (e) {
        handleHttpError(e);
        return [] as unknown as SupportedCurrency[];
      }
    };

    const getVerificationLink = async () => {
      try {
        const result = await getPaymentProfileService().getVerificationLink(returnUri, refreshUri);
        verificationLink.value = result.verificationUri;
        return result;
      } catch (e) {
        handleHttpError(e);
        return Promise.reject(e);
      }
    };

    const hasPresentmentCurrency = computed(() => {
      return isPaymentProfileVerified.value && paymentProfile.value?.presentmentCurrency;
    });

    const presentmentCurrency = computed(() => {
      return paymentProfile.value?.presentmentCurrency as ArcualCurrency;
    });

    const setupSseListener = async () =>
      getPaymentProfileService().addSseConnection(async (sseEventPayload: PaymentProfileSseEventPayload) => {
        console.debug('paymentProfileSSE', sseEventPayload);
        const store = usePaymentProfileStore();
        switch (sseEventPayload.type) {
          case PaymentProfileEvent.PAYMENT_PROFILE_UPDATED:
            store.handlePaymentProfileUpdatedEvent(sseEventPayload.data);
            break;
        }
      });

    /**
     * Reset the store to its initial state
     */
    const reset = () => {
      paymentProfile.value = undefined;
      verificationLink.value = undefined;
      hasDismissedPaymentProfileBanner.value = false;
      currentPaymentSystem.value = undefined;
      paymentProfileBankDetails.value = undefined;
      payoutCurrencies.value = [];
    };

    return {
      paymentProfile,
      salesCurrencies,
      payoutCurrencies,
      verificationLink,
      isPaymentProfileVerified,
      hasDismissedPaymentProfileBanner,
      currentPaymentSystem,
      hasPresentmentCurrency,
      presentmentCurrency,
      loadPaymentProfile,
      updatePaymentProfile,
      onboardPaymentProfile,
      getVerificationLink,
      setupSseListener,
      handlePaymentProfileUpdatedEvent,
      checkPaymentStatus,
      loadPayoutCurrencies,
      reset,
      loadPaymentBankDetails,
      paymentProfileBankDetails,
    };
  },
  { persist: { paths: ['hasDismissedPaymentProfileBanner'] } },
);
